import { useState, useCallback } from "react";
import BaseApi from "../services/base-api";
import { useToast } from "../context/ToastContext";
import { useTranslation } from "react-i18next";
import { LocalizedText } from "../types/LocalizedText";

// ==================== TYPES ====================
export interface CartItem {
  id: string;
  productId: string;
  quantity: number;
  price: number;
  discount: number;
  displayName: string;
  displayImage: string;
  isAvailable: boolean;
  isLowStock: boolean;
  availableStock: number;
  stockMessage: string;
  variation?: {
    id: string;
    weight: number;
    attributes: {
      color?: string;
      size?: string;
    };
  };
  product: {
    id: string;
    name: LocalizedText
    images: string[];
  };
}

export interface Cart {
  id: string;
  userId: string;
  sessionId: string;
  items: CartItem[];
  subtotal: number;
  shipping: number;
  discount: number;
  discountCode: string;
  tax: number;
  total: number;
  createdAt: string;
  updatedAt: string;
}

const EMPTY_CART: Cart = {
  id: "",
  userId: "",
  sessionId: "",
  items: [],
  shipping: 0,
  subtotal: 0,
  discount: 0,
  discountCode: "",
  tax: 0,
  total: 0,
  createdAt: "",
  updatedAt: "",
};

// ==================== CALCULATIONS ====================
export const FREE_SHIPPING_THRESHOLD = 500;

export const calculateCartTotals = (cart: Cart) => {
  if (!cart || !cart.items.length) {
    return {
      subtotal: 0,
      discount: 0,
      tax: 0,
      shipping: 0,
      total: 0,
      totalItems: 0,
    };
  }

  const subtotal = cart.subtotal;
  const discount = cart.discount;
  const tax = cart.tax;
  const shipping = subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : cart.shipping;
  const total = cart.total + shipping;
  const totalItems = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  return { subtotal, discount, tax, shipping, total, totalItems };
};

export const calculateShippingProgress = (subtotal: number) => {
  const progress = subtotal >= FREE_SHIPPING_THRESHOLD 
    ? 100 
    : (subtotal / FREE_SHIPPING_THRESHOLD) * 100;
  
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);

  return {
    progress,
    remaining,
    isFreeShipping: subtotal >= FREE_SHIPPING_THRESHOLD,
  };
};

// Helper function to recalculate cart totals after optimistic update
const recalculateCartTotals = (cart: Cart): Cart => {
  const subtotal = cart.items.reduce((sum, item) => {
    const price = Number(item.price) || 0;
    const quantity = Number(item.quantity) || 0;
    const discountPercent = Number(item.discount) || 0;

    const itemTotal = price * quantity;
    const itemDiscount = (discountPercent / 100) * itemTotal;

    return sum + (itemTotal - itemDiscount);
  }, 0);

  const discount = Number(cart.discount) || 0;
  const tax = Number(cart.tax) || 0;
  const currentShipping = Number(cart.shipping) || 0;

  const shipping = subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : currentShipping;

  const total = subtotal - discount + tax + shipping;

  return {
    ...cart,
    subtotal: Number(subtotal.toFixed(2)),
    shipping: Number(shipping.toFixed(2)),
    total: Number(total.toFixed(2)),
  };
};

// ==================== CART HOOK ====================
export const useCart = () => {
  const [cart, setCart] = useState<Cart>(EMPTY_CART);
  const [loading, setLoading] = useState(true);
  const [updatingItemId, setUpdatingItemId] = useState<string | null>(null);
  const [removingItemId, setRemovingItemId] = useState<string | null>(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingEstimatedDays, setShippingEstimatedDays] = useState("");
  const [destination, setDestination] = useState("");

  const { success, err } = useToast();
  const { t } = useTranslation();

  const calculateTotalWeight = (cartData: Cart): number => {
    return cartData.items.reduce((total, item) => {
      const weightPerItem = (item as CartItem)?.variation?.weight || 0;
      return total + weightPerItem * item.quantity;
    }, 0);
  };

  const fetchShippingRates = useCallback(async (cartData: Cart, destinationName: string) => {
    try {
      const weight = calculateTotalWeight(cartData);
      if (!weight) {
        setShippingEstimatedDays("");
        return;
      }

      setShippingLoading(true);
      const res = await BaseApi.post("/shipping/rates", {
        weight,
        destination: destinationName,
      });

      if (res.success && res.data) {
        setShippingEstimatedDays(res.data.estimatedDays || "");
        const updatedCart = {
          ...cartData,
          shipping: Number(res.data.rate),
        };
        setCart(recalculateCartTotals(updatedCart));
      } else {
        setShippingEstimatedDays("");
      }
    } catch (error) {
      setShippingEstimatedDays("");
      console.error("Failed to fetch shipping rates:", error);
    } finally {
      setShippingLoading(false);
    }
  }, []);

  const loadCart = useCallback(async (destinationOverride?: string) => {
    const finalDestination = destinationOverride ?? destination;

    try {
      setLoading(true);
      const res = await BaseApi.get("/cart");

      if (res.success && res.data) {
        setCart(res.data);
        fetchShippingRates(res.data, finalDestination);
      } else {
        setCart(EMPTY_CART);
      }
    } catch (error) {
      console.error("Failed to load cart:", error);
      setCart(EMPTY_CART);
    } finally {
      setLoading(false);
    }
  }, [destination, fetchShippingRates]);

  const updateQuantity = useCallback(async (itemId: string, delta: number) => {
    const item = cart.items.find((i) => i.id === itemId);
    if (!item) return { shouldRemove: false };

    const newQuantity = Math.max(0, item.quantity + delta);

    if (newQuantity === 0) {
      return { shouldRemove: true };
    }

    if (newQuantity > item.availableStock) {
      err(t("Cannot add more items. Only {{stock}} available in stock.", { 
        stock: item.availableStock 
      }));
      return { shouldRemove: false };
    }

    // Store previous state for rollback
    const previousCart = cart;

    // Optimistic update - update UI immediately
    const optimisticCart = {
      ...cart,
      items: cart.items.map((i) =>
        i.id === itemId ? { ...i, quantity: newQuantity } : i
      ),
    };
    const recalculatedCart = recalculateCartTotals(optimisticCart);
    setCart(recalculatedCart);
    fetchShippingRates(recalculatedCart, destination);
    setUpdatingItemId(itemId);

    // Make API call in background
    BaseApi.put(`/cart/items/${itemId}`, { quantity: newQuantity })
      .then((res) => {
        if (res.success && res.data) {
          // Merge server data but preserve isAvailable from previous cart —
          // the PUT endpoint may not return isAvailable, which would cause
          // checkout to incorrectly block with "unavailable items" error.
          const mergedCart: Cart = {
            ...res.data,
            items: (res.data.items || []).map((newItem: CartItem) => {
              const prevItem = previousCart.items.find((i) => i.id === newItem.id);
              return {
                ...newItem,
                isAvailable: newItem.isAvailable ?? prevItem?.isAvailable ?? true,
              };
            }),
          };
          setCart(mergedCart);
          window.dispatchEvent(
            new CustomEvent("cartUpdated", {
              detail: { action: delta > 0 ? "add" : "decrease", itemId },
            })
          );
          success(t(delta > 0 ? "Item quantity increased" : "Item quantity decreased"));
        } else {
          throw new Error(res.message || "Failed to update quantity");
        }
      })
      .catch((error: any) => {
        console.error("Failed to update quantity:", error);
        // Rollback on error
        setCart(previousCart);
        console.error(error?.response?.data?.message || t("Failed to update quantity. Please try again."));
      })
      .finally(() => {
        setUpdatingItemId(null);
      });

    return { shouldRemove: false };
  }, [cart, t, err, success]);

  const removeItem = useCallback(async (itemId: string) => {
    // Store previous state for rollback
    const previousCart = cart;

    // Optimistic update - remove item immediately
    const optimisticCart = {
      ...cart,
      items: cart.items.filter((i) => i.id !== itemId),
    };
    const recalculatedCart = recalculateCartTotals(optimisticCart);
    setCart(recalculatedCart);
    setRemovingItemId(itemId);
    fetchShippingRates(recalculatedCart, destination);

    // Make API call in background
    BaseApi.delete(`/cart/items/${itemId}`)
      .then((res) => {
        if (res.success && res.data) {
          // Update with server data
          setCart(res.data);
          window.dispatchEvent(
            new CustomEvent("cartUpdated", {
              detail: { action: "remove", itemId },
            })
          );
          success(t("Item removed from cart"));
        } else {
          throw new Error(res.message || "Failed to remove item");
        }
      })
      .catch((error: any) => {
        console.error("Failed to remove item:", error);
        // Rollback on error
        setCart(previousCart);
        console.error(error?.response?.data?.message || t("Failed to remove item. Please try again."));
      })
      .finally(() => {
        setRemovingItemId(null);
      });
  }, [cart, t, err, success]);

  const clearCart = useCallback(async () => {
    // Store previous state for rollback
    const previousCart = cart;

    // Optimistic update
    setCart(EMPTY_CART);

    // Make API call in background
    BaseApi.delete("/cart/clear")
      .then((res) => {
        if (res.success) {
          window.dispatchEvent(
            new CustomEvent("cartUpdated", {
              detail: { action: "clear" },
            })
          );
          success(t("Cart cleared successfully"));
        } else {
          throw new Error(res.message || "Failed to clear cart");
        }
      })
      .catch((error: any) => {
        console.error("Failed to clear cart:", error);
        // Rollback on error
        setCart(previousCart);
        console.error(error?.response?.data?.message || t("Failed to clear cart. Please try again."));
      });
  }, [cart, t, err, success]);

  const applyPromoCode = useCallback(async (code: string) => {
    try {
      const res = await BaseApi.post("/cart/apply-coupon", {
        code: code.trim(),
      });

      if (res.success && res.data) {
        setCart(res.data);
        success(t("Promo code applied successfully!"));
        return { success: true };
      } else {
        return { success: false, error: res.message || t("Invalid promo code") };
      }
    } catch (error: any) {
      console.error("Failed to apply promo code:", error);
      const errorMessage = error?.response?.data?.message || t("Failed to apply promo code. Please try again.");
      return { success: false, error: errorMessage };
    }
  }, [t, success]);

  const removePromoCode = useCallback(async () => {
    // Store previous state for rollback
    const previousCart = cart;

    // Optimistic update
    const optimisticCart = {
      ...cart,
      discount: 0,
      discountCode: "",
    };
    const recalculatedCart = recalculateCartTotals(optimisticCart);
    setCart(recalculatedCart);

    // Make API call in background
    BaseApi.delete("/cart/remove-coupon")
      .then((res) => {
        if (res.success && res.data) {
          setCart(res.data);
          success(t("Promo code removed"));
        } else {
          throw new Error(res.message || "Failed to remove coupon");
        }
      })
      .catch((error: any) => {
        console.error("Failed to remove promo code:", error);
        // Rollback on error
        setCart(previousCart);
        err(error?.response?.data?.message || t("Failed to remove promo code. Please try again."));
      });
  }, [cart, t, err, success]);

  return {
    cart,
    loading,
    updatingItemId,
    removingItemId,
    loadCart,
    updateQuantity,
    removeItem,
    clearCart,
    applyPromoCode,
    removePromoCode,
    fetchShippingRates,
    shippingLoading,
    shippingEstimatedDays,
    destination,
    setDestination,
  };
};